import { useState, useEffect } from 'react';
import { 
  Send, 
  Calendar, 
  Clock, 
  Play, 
  Settings2, 
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  CalendarDays
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { getApiUrl } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface MailerConfig {
  enabled: boolean;
  cronTime: string; // formato HH:MM
  dateMode: 'yesterday' | 'today' | 'custom';
  customStartDate?: string | null;
  customEndDate?: string | null;
  lastRun?: string;
  lastStatus?: 'success' | 'error';
  lastMessage?: string;
}

interface EmailSettings {
  productionMode: boolean;
  testEmail: string;
}

interface DateRange {
  startDate: string;
  endDate: string;
}

const MAILER_TIME_ZONE = 'America/Sao_Paulo';

function getISODateInTimeZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function addDaysISO(dateISO: string, deltaDays: number): string {
  const [yyyy, mm, dd] = dateISO.split('-').map(Number);
  const dt = new Date(Date.UTC(yyyy, (mm || 1) - 1, dd || 1, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().split('T')[0];
}

function getDefaultManualRange(): DateRange {
  const today = getISODateInTimeZone(new Date(), MAILER_TIME_ZONE);
  const yesterday = addDaysISO(today, -1);
  return { startDate: yesterday, endDate: yesterday };
}

function isValidDateISO(dateISO: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return false;
  const [yyyy, mm, dd] = dateISO.split('-').map(Number);
  const dt = new Date(Date.UTC(yyyy, mm - 1, dd));
  return dt.getUTCFullYear() === yyyy && dt.getUTCMonth() === mm - 1 && dt.getUTCDate() === dd;
}

function formatDateBR(dateISO: string): string {
  const [yyyy, mm, dd] = dateISO.split('-');
  return `${dd}/${mm}/${yyyy}`;
}

function formatDateRange(range: DateRange): string {
  if (!range.startDate || !range.endDate) return 'Período não definido';
  if (range.startDate === range.endDate) return formatDateBR(range.startDate);
  return `${formatDateBR(range.startDate)} a ${formatDateBR(range.endDate)}`;
}

function resolveConfigRange(config: MailerConfig): DateRange | null {
  const today = getISODateInTimeZone(new Date(), MAILER_TIME_ZONE);
  const yesterday = addDaysISO(today, -1);

  if (config.dateMode === 'yesterday') {
    return { startDate: yesterday, endDate: yesterday };
  }

  if (config.dateMode === 'today') {
    return { startDate: today, endDate: today };
  }

  if (config.customStartDate && config.customEndDate) {
    return { startDate: config.customStartDate, endDate: config.customEndDate };
  }

  return null;
}

export function AdminMailerPage() {
  const [config, setConfig] = useState<MailerConfig>({
    enabled: false,
    cronTime: '08:00',
    dateMode: 'yesterday',
  });
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    productionMode: false,
    testEmail: 'leonardo.camelo@crasdata.com.br',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [manualRange, setManualRange] = useState<DateRange>(() => getDefaultManualRange());
  
  const { toast } = useToast();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async (options: { syncManualRange?: boolean } = { syncManualRange: true }) => {
    setIsLoading(true);
    try {
      // Carregar configuração do mailer
      const mailerResponse = await fetch(getApiUrl('/claro/api/email/mailer-config'));
      if (!mailerResponse.ok) throw new Error(`HTTP ${mailerResponse.status}`);
      const mailerData = await mailerResponse.json();
      if (mailerData.status === 'success' && mailerData.config) {
        const loadedConfig = mailerData.config as MailerConfig;
        setConfig(loadedConfig);

        if (options.syncManualRange !== false) {
          const configuredRange = resolveConfigRange(loadedConfig);
          if (configuredRange) {
            setManualRange(configuredRange);
          }
        }
      }

      // Carregar configuração de modo produção/teste
      const settingsResponse = await fetch(getApiUrl('/claro/api/email/regionals-config'));
      if (!settingsResponse.ok) throw new Error(`HTTP ${settingsResponse.status}`);
      const settingsData = await settingsResponse.json();
      if (settingsData.status === 'success' && settingsData.settings) {
        setEmailSettings(settingsData.settings);
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar configuração',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(getApiUrl('/claro/api/email/mailer-config'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        toast({
          title: 'Configuração salva',
          description: 'As configurações do Mailer foram atualizadas.',
        });
      } else {
        throw new Error(data.message || 'Erro ao salvar');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao salvar configuração',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const sendNow = async () => {
    const startDate = manualRange.startDate.trim();
    const endDate = manualRange.endDate.trim();

    if (!startDate || !endDate) {
      toast({
        title: 'Datas obrigatórias',
        description: 'Informe a data inicial e final para o disparo manual.',
        variant: 'destructive',
      });
      return;
    }

    if (!isValidDateISO(startDate) || !isValidDateISO(endDate)) {
      toast({
        title: 'Datas inválidas',
        description: 'Use datas válidas no formato YYYY-MM-DD.',
        variant: 'destructive',
      });
      return;
    }

    if (startDate > endDate) {
      toast({
        title: 'Período inválido',
        description: 'A data inicial não pode ser maior que a data final.',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(getApiUrl('/claro/api/email/send-now'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDate, endDate }),
      });
      const data = await response.json().catch(() => null);
      
      if (response.ok && data?.status === 'success') {
        toast({
          title: 'Emails enviados!',
          description: data.message || `Relatório gerado e emails enviados com sucesso.`,
        });
        loadConfig({ syncManualRange: false }); // Recarregar para atualizar lastRun
      } else {
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
    } catch (error) {
      toast({
        title: 'Erro ao enviar',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const getDateDescription = () => {
    if (config.dateMode === 'yesterday') {
      const yesterday = addDaysISO(getISODateInTimeZone(new Date(), MAILER_TIME_ZONE), -1);
      return `Ontem (${formatDateBR(yesterday)})`;
    } else if (config.dateMode === 'today') {
      return `Hoje (${formatDateBR(getISODateInTimeZone(new Date(), MAILER_TIME_ZONE))})`;
    } else {
      if (config.customStartDate && config.customEndDate) {
        if (config.customStartDate === config.customEndDate) {
          return formatDateBR(config.customStartDate);
        }
        return `${formatDateBR(config.customStartDate)} a ${formatDateBR(config.customEndDate)}`;
      }
      return 'Período personalizado incompleto';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Mailer</h2>
          <p className="text-muted-foreground">
            Configure e dispare emails de processos finalizados
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => loadConfig()} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
          <Button onClick={saveConfig} disabled={isSaving}>
            <Settings2 className="mr-2 h-4 w-4" />
            {isSaving ? 'Salvando...' : 'Salvar Configuração'}
          </Button>
        </div>
      </div>

      {/* Status do Modo */}
      <Card className={emailSettings.productionMode ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-amber-500 bg-amber-50 dark:bg-amber-950'}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            {emailSettings.productionMode ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            )}
            <div>
              <h3 className="font-semibold">
                {emailSettings.productionMode ? 'Modo Produção' : 'Modo Teste'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {emailSettings.productionMode 
                  ? 'Os emails serão enviados para todos os destinatários cadastrados.'
                  : `Os emails serão enviados apenas para ${emailSettings.testEmail}`
                }
              </p>
            </div>
            <Badge variant={emailSettings.productionMode ? 'default' : 'secondary'} className="ml-auto">
              {emailSettings.productionMode ? 'PRODUÇÃO' : 'TESTE'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Configuração do Cron */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Agendamento Automático
            </CardTitle>
            <CardDescription>
              Configure o horário para envio automático diário
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="cron-enabled">Ativar agendamento</Label>
              <Switch
                id="cron-enabled"
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cron-time">Horário de execução</Label>
              <Input
                id="cron-time"
                type="time"
                value={config.cronTime}
                onChange={(e) => setConfig(prev => ({ ...prev, cronTime: e.target.value }))}
                disabled={!config.enabled}
              />
              <p className="text-xs text-muted-foreground">
                O relatório será gerado e enviado automaticamente neste horário
              </p>
            </div>

            {config.lastRun && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Última execução: {new Date(config.lastRun).toLocaleString('pt-BR')}
                </p>
                {config.lastStatus && (
                  <Badge variant={config.lastStatus === 'success' ? 'default' : 'destructive'} className="mt-1">
                    {config.lastStatus === 'success' ? 'Sucesso' : 'Erro'}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuração de Data */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Período do Relatório
            </CardTitle>
            <CardDescription>
              Defina qual período será consultado
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Modo de data</Label>
              <Select
                value={config.dateMode}
                onValueChange={(value: 'yesterday' | 'today' | 'custom') => 
                  setConfig(prev => ({ ...prev, dateMode: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yesterday">Ontem</SelectItem>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="custom">Período personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.dateMode === 'custom' && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Data inicial</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={config.customStartDate || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, customStartDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">Data final</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={config.customEndDate || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, customEndDate: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <div className="pt-2">
              <Badge variant="outline" className="gap-2">
                <CalendarDays className="h-3 w-3" />
                {getDateDescription()}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disparo Manual */}
      <Card className="border-2 border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Disparo Manual
          </CardTitle>
          <CardDescription>
            Execute o envio imediatamente com as configurações atuais
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="manual-start-date">Data inicial</Label>
              <Input
                id="manual-start-date"
                type="date"
                value={manualRange.startDate}
                onChange={(e) => setManualRange(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manual-end-date">Data final</Label>
              <Input
                id="manual-end-date"
                type="date"
                value={manualRange.endDate}
                onChange={(e) => setManualRange(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm">
                <strong>Período:</strong> {formatDateRange(manualRange)}
              </p>
              <p className="text-sm text-muted-foreground">
                {emailSettings.productionMode 
                  ? 'Emails serão enviados para todos os destinatários'
                  : `Emails serão enviados apenas para ${emailSettings.testEmail}`
                }
              </p>
            </div>
            <Button 
              size="lg" 
              onClick={sendNow} 
              disabled={isSending || !manualRange.startDate || !manualRange.endDate}
              className="gap-2 sm:self-end"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Disparar Agora
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
